import React, { useRef, useState, useCallback } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { OrderDocument } from '@lds-efsp/shared-contracts';

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

interface DocumentUploaderProps {
  brand: BrandConfig;
  documents: OrderDocument[];
  onUpload: (file: File, category?: string) => Promise<OrderDocument>;
  onRemove: (docId: string) => void;
  accept?: string;
}

const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  brand,
  documents,
  onUpload,
  onRemove,
  accept = '.pdf,.png,.jpg,.jpeg,.tiff,.doc,.docx',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState<Set<string>>(() => new Set());
  const [errors, setErrors] = useState<string[]>([]);

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return `${file.name}: Unsupported file type (${file.type})`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `${file.name}: File exceeds 50MB limit`;
    }
    return null;
  };

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newErrors: string[] = [];

    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        newErrors.push(error);
        continue;
      }

      const uploadId = crypto.randomUUID();
      setUploading(prev => new Set(prev).add(uploadId));
      try {
        await onUpload(file);
      } catch {
        newErrors.push(`${file.name}: Upload failed`);
      } finally {
        setUploading(prev => {
          const next = new Set(prev);
          next.delete(uploadId);
          return next;
        });
      }
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      setTimeout(() => setErrors([]), 5000);
    }
  }, [onUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
      e.target.value = '';
    }
  }, [handleFiles]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors"
        style={{
          borderColor: dragOver ? brand.colors.primary : brand.colors.border,
          backgroundColor: dragOver ? `${brand.colors.primary}08` : 'transparent',
        }}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple
          onChange={handleFileChange}
          className="hidden"
          aria-label="Upload documents"
        />
        <p className="text-sm" style={{ color: brand.colors.textLight }}>
          Drag and drop files here, or click to browse
        </p>
        <p className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
          PDF, PNG, JPEG, TIFF, DOC, DOCX — Max 50MB per file
        </p>
      </div>

      {/* Upload progress */}
      {uploading.size > 0 && (
        <div className="mt-3">
          <p className="text-xs" style={{ color: brand.colors.link }}>
            Uploading {uploading.size} file(s)...
          </p>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="mt-3 p-2 rounded-lg" style={{ backgroundColor: '#FEF2F2' }}>
          {errors.map((err) => (
            <p key={err} className="text-xs text-red-600">{err}</p>
          ))}
        </div>
      )}

      {/* File list */}
      {documents.length > 0 && (
        <div className="mt-4 space-y-2">
          {documents.map(doc => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-3 border rounded-lg"
              style={{ borderColor: brand.colors.border }}
            >
              <div>
                <p className="text-sm font-medium" style={{ color: brand.colors.text }}>
                  {doc.fileName}
                </p>
                <p className="text-xs" style={{ color: brand.colors.textLight }}>
                  {formatFileSize(doc.size)}
                  {doc.category && ` \u00B7 ${doc.category}`}
                </p>
              </div>
              <button
                onClick={() => onRemove(doc.id)}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentUploader;
